// Compra real de las PDPs de tienda (misión L2, 2026-11-03): geles, Kit Completo y Ritual Brush.
// sections/skindion-tienda-hero.liquid y sections/skindion-tienda-cards.liquid.
//
// Lo que se cobra es el precio de Shopify: el selector de geles solo suma el precio real de cada gel y agrega esas
// variantes. Sin descuentos, tachados ni planes propios (reglas de CLAUDE.md). El carrito lo cambia mutateCart de
// assets/skindion-cart.js, el mismo camino que la oferta, así el drawer sigue teniendo un solo controlador.
//
// La parte pura (window.SkindionTienda) se prueba en Node: tools/tienda.test.mjs.
(() => {
  const T = (window.SkindionTienda = window.SkindionTienda || {
    /** @param {number} cents - «$1,100», con centavos solo si los hay. */
    money(cents) {
      const value = Number(cents) / 100;
      const [whole, decimals] = (Number.isInteger(value) ? String(value) : value.toFixed(2)).split('.');
      return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${decimals ? `.${decimals}` : ''}`;
    },

    /** Arranque: 2 geles, uno de cada uno, con el gel de la página primero. La tarjeta 3 suma otro del gel de la página. */
    initialState(entry = 'H') {
      return { tier: 2, memory: entry === 'R' ? ['R', 'H', 'R'] : ['H', 'R', 'H'] };
    },

    slots(state, tier = state.tier) {
      return state.memory.slice(0, tier);
    },

    total(slots, prices) {
      return slots.reduce((sum, gel) => sum + prices[gel], 0);
    },

    /** Una línea por gel, con su cantidad: lo que recibe el carrito. El orden sigue a la selección. */
    lines(slots, variants) {
      const counts = new Map();
      for (const gel of slots) counts.set(gel, (counts.get(gel) ?? 0) + 1);
      return [...counts].map(([gel, quantity]) => ({ id: variants[gel], quantity }));
    },

    /** Gel elegido sin existencias, o null. */
    unavailable(slots, available) {
      return slots.find((gel) => !available[gel]) ?? null;
    },

    /** «2 geles · Hidratante + Regenerativo» o «3 geles · 2 Hidratantes + Regenerativo». */
    composition(slots, names) {
      const counts = new Map();
      for (const gel of slots) counts.set(gel, (counts.get(gel) ?? 0) + 1);
      const parts = [...counts].map(([gel, n]) => (n === 1 ? names[gel] : `${n} ${names[gel]}s`));
      return `${slots.length === 1 ? '1 gel' : `${slots.length} geles`} · ${parts.join(' + ')}`;
    },
  });

  const ADD_ERROR = 'No pudimos agregar el producto. Revisa tu conexión e intenta de nuevo.';

  /** @param {{ id: number, quantity: number }[]} items @param {Element} source */
  async function addToCart(items, source) {
    const { mutateCart } = await import('@theme/skindion-cart');
    return mutateCart({ add: items, action: 'add', source });
  }

  const errorText = (error) => error?.response?.description || error?.response?.message || ADD_ERROR;

  /**
   * Formularios de compra sueltos (tarjetas de «Completa tu ritual»). Sin JS, el formulario nativo también agrega.
   * Solo se engancha una vez aunque haya varias secciones en la página.
   */
  if (!window.__skindionTiendaForms) {
    window.__skindionTiendaForms = true;
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-skt-add-form]')) return;
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      const error = form.querySelector('[data-skt-error]');
      if (button?.getAttribute('aria-busy') === 'true') return;
      button?.setAttribute('aria-busy', 'true');
      if (error) error.textContent = '';
      try {
        await addToCart([{ id: Number(form.elements.id.value), quantity: 1 }], form);
      } catch (e) {
        if (error) error.textContent = errorText(e);
      } finally {
        button?.removeAttribute('aria-busy');
      }
    });
  }

  if (customElements.get('skindion-tienda-buy')) return;

  /**
   * <skindion-tienda-buy>: un solo estado para el buybox y su sticky (que vive en <body>).
   * data-mode="gels": selector de 1 a 3 geles con mezcla. data-mode="single": una variante. El HTML ya trae el estado
   * inicial (Liquid), y sin JS el formulario nativo agrega el producto de la página.
   */
  class SkindionTiendaBuy extends HTMLElement {
    #config = null;
    #state = null;
    #sticky = null;
    #abort = null;
    #adding = false;
    #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      try {
        this.#config = JSON.parse(this.querySelector('[data-skt-config]')?.textContent || 'null');
      } catch {
        this.#config = null;
      }
      if (!this.#config) return;

      if (this.dataset.mode === 'gels') {
        this.#state = T.initialState(this.#config.entry);
        this.addEventListener('change', (event) => this.#onChange(event), { signal });
      }

      // `position: fixed` falla dentro de contenedores con transform: el sticky vive en <body>.
      this.#sticky ??= this.querySelector('[data-skt-sticky]');
      if (this.#sticky) document.body.appendChild(this.#sticky);

      this.querySelector('form')?.addEventListener('submit', (event) => this.#onAdd(event, this), { signal });
      this.#sticky?.querySelector('[data-skt-sticky-cta]')?.addEventListener('click', (event) => this.#onAdd(event, this.#sticky), { signal });
      this.#sticky?.querySelector('[data-skt-sticky-focus]')?.addEventListener('click', () => this.#goToBuybox(), { signal });
      // CTA de cierre de la landing (sections/skindion-geles-v2-razones.liquid): sube al selector sin perder la selección.
      document.addEventListener('click', (event) => this.#onDocumentClick(event), { signal });

      this.#setupSticky(signal);
      this.#setupGallery(signal);
      if (this.#state) this.#render('');
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#sticky?.remove();
      document.documentElement.classList.remove('pdp-sticky-on');
    }

    #onDocumentClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-geles-v2-goto]')) return;
      event.preventDefault();
      this.#goToBuybox();
    }

    #goToBuybox() {
      const anchor = this.querySelector('[data-skt-anchor]') || this;
      anchor.scrollIntoView({ behavior: this.#reduced.matches ? 'auto' : 'smooth', block: 'start' });
      const focusable = this.querySelector('input[data-skt-tier]:checked') || this.querySelector('button[type="submit"]');
      focusable?.focus({ preventScroll: true });
    }

    #onChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.dataset.sktTier !== undefined) {
        this.#state = { ...this.#state, tier: Number(input.value) };
      } else if (input.dataset.sktSlot !== undefined) {
        const memory = [...this.#state.memory];
        memory[Number(input.dataset.sktSlot)] = input.value;
        this.#state = { ...this.#state, memory };
      } else return;
      const { names } = this.#config.copy;
      const slots = T.slots(this.#state);
      this.#render(`${T.composition(slots, names)}: ${T.money(T.total(slots, this.#config.prices))}`);
    }

    #render(message) {
      const { prices, available, copy } = this.#config;
      const slots = T.slots(this.#state);
      const total = T.total(slots, prices);
      this.dataset.tier = String(this.#state.tier);

      for (const card of this.querySelectorAll('[data-skt-card]')) {
        const tier = Number(card.dataset.sktCard);
        const active = tier === this.#state.tier;
        const cardSlots = T.slots(this.#state, tier);
        card.dataset.active = String(active);
        const radio = card.querySelector('input[data-skt-tier]');
        if (radio) radio.checked = active;
        const price = card.querySelector('[data-skt-card-price]');
        if (price) price.textContent = T.money(T.total(cardSlots, prices));
        card.querySelectorAll('[data-bottle]').forEach((bottle, i) => (bottle.dataset.gel = cardSlots[i] ?? ''));
      }

      // La mezcla por botella vive dentro de la tarjeta activa y se mueve con ella.
      const mix = this.querySelector('[data-skt-mix]');
      const activeCard = this.querySelector(`[data-skt-card="${this.#state.tier}"]`);
      if (mix && activeCard && mix.parentElement !== activeCard) activeCard.appendChild(mix);
      for (const row of this.querySelectorAll('[data-skt-slot-row]')) {
        const index = Number(row.dataset.sktSlotRow);
        row.hidden = index >= this.#state.tier;
        for (const input of row.querySelectorAll('input')) input.checked = input.value === this.#state.memory[index];
      }

      const out = T.unavailable(slots, available);
      const error = this.querySelector('[data-skt-error]');
      if (error) {
        error.textContent = out ? copy.oos.replace('{gel}', copy.names[out]) : '';
        error.hidden = !out;
      }
      const cta = this.querySelector('button[type="submit"]');
      const label = this.querySelector('[data-skt-cta-label]');
      if (label) label.textContent = copy.cta.replace('{price}', T.money(total));
      if (cta) cta.disabled = Boolean(out);

      if (this.#sticky) {
        const stickyCta = this.#sticky.querySelector('[data-skt-sticky-cta]');
        if (stickyCta) stickyCta.disabled = Boolean(out);
        const summary = this.#sticky.querySelector('[data-skt-sticky-summary]');
        if (summary) summary.textContent = T.composition(slots, copy.names);
        const price = this.#sticky.querySelector('[data-skt-sticky-price]');
        if (price) price.textContent = T.money(total);
      }
      const live = this.querySelector('[data-skt-live]');
      if (live && message) live.textContent = message;
    }

    #items() {
      if (this.#state) return T.lines(T.slots(this.#state), this.#config.variants);
      return [{ id: this.#config.variant, quantity: 1 }];
    }

    /** @param {Event} event @param {Element} scope - El buybox o el sticky: su zona de error es la que habla. */
    async #onAdd(event, scope) {
      event.preventDefault();
      if (this.#adding) return;
      const buttons = [this.querySelector('button[type="submit"]'), this.#sticky?.querySelector('[data-skt-sticky-cta]')];
      if (buttons[0]?.disabled) return;
      const errors = [this.querySelector('[data-skt-error]'), this.#sticky?.querySelector('[data-skt-error]')];
      this.#adding = true;
      for (const b of buttons) b?.setAttribute('aria-busy', 'true');
      for (const e of errors) if (e) { e.textContent = ''; e.hidden = true; }
      try {
        await addToCart(this.#items(), scope);
      } catch (e) {
        const zone = scope.querySelector('[data-skt-error]') || errors[0];
        if (zone) { zone.textContent = errorText(e); zone.hidden = false; }
      } finally {
        this.#adding = false;
        for (const b of buttons) b?.removeAttribute('aria-busy');
      }
    }

    // Como el sticky de la promo: se mide en cada scroll (Horizon desplaza dentro de .page-wrapper en escritorio) y se
    // oculta al llegar al footer. La burbuja de Chatwoot sube con la clase pdp-sticky-on.
    #setupSticky(signal) {
      const cta = this.querySelector('button[type="submit"]');
      if (!cta || !this.#sticky) return;
      const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
      let frame = 0;
      const measure = () => {
        frame = 0;
        const visible = cta.getBoundingClientRect().bottom < 0 && !(footer ? footer.getBoundingClientRect().top < window.innerHeight : false);
        this.#sticky.dataset.stuck = String(visible);
        this.#sticky.inert = !visible;
        document.documentElement.classList.toggle('pdp-sticky-on', visible);
        if (visible) document.documentElement.style.setProperty('--pdp-sticky-h', `${this.#sticky.offsetHeight}px`);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(measure);
      };
      document.addEventListener('scroll', schedule, { capture: true, passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      measure();
    }

    // Galería: carril con scroll-snap (deslizar en móvil) y miniaturas que siguen la foto visible.
    #setupGallery(signal) {
      const gallery = this.closest('.skg2-hero')?.querySelector('[data-skg2-gallery]');
      if (!gallery) return;
      const track = gallery.querySelector('[data-skg2-track]');
      const slides = [...gallery.querySelectorAll('[data-skg2-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-skg2-thumb]')];
      if (!track || slides.length < 2) return;
      const select = (index) => thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) if (e.isIntersecting && e.intersectionRatio > 0.6) select(slides.indexOf(e.target));
        },
        { root: track, threshold: [0.6] }
      );
      slides.forEach((s) => io.observe(s));
      signal.addEventListener('abort', () => io.disconnect());
      thumbs.forEach((thumb, i) =>
        thumb.addEventListener(
          'click',
          () => {
            track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: this.#reduced.matches ? 'auto' : 'smooth' });
            select(i);
          },
          { signal }
        )
      );
    }
  }

  customElements.define('skindion-tienda-buy', SkindionTiendaBuy);
})();
